import {
	map,
	when,
	propEq,
	find, findIndex, path, equals, prop, pluck, filter,
} from 'ramda';
import Vue from 'vue';
import bus from '@/bus/app-bus';
import {
	parseHierarchyAssignation,
	parseAssignationRequest,
	commandMap,
	parseOrganizationChart,
	parseJobs,
} from '@/modules/job-profile/utils/helpers';
import { calculateJobProgress } from '@/modules/job-profile/helpers/helpers';

const LOADER_EVENT = 'app-event-loader-toggle-view';
const LOADER_MESSAGE = 'Praktiko esta asignando las jerarquias.';
const LOADER_MESSAGE_ORGANIZATIONAL_CHART = 'Praktiko esta generando el organizagrama.';

// Pending promise for loadGroups deduplication
let pendingLoadGroups = null;

export default {
	async getJobValuationStatus({ commit }) {
		try {
			const { status, data } = await Vue.prototype.$http.get('/polls/JEV');
			const hasInformation = status === 200;
			commit('UPDATE_HAS_INFORMATION', hasInformation);
			if (hasInformation) {
				commit('UPDATE_INFORMATION_JOB_EVALUATION', data);
			}
			return true;
		} catch (e) {
			throw e;
		}
	},
	updateJobList({ commit }, jobList) {
		// Asegurar que todos los jobs tengan el progreso calculado
		const jobsWithProgress = map(job => ({
			...job,
			progress: calculateJobProgress(job),
		}), jobList);
		commit('UPDATE_JOB_LIST', jobsWithProgress);
	},
	updateJobItem({ commit, state }, job) {
		const jobWithProgress = {
			...job,
			progress: calculateJobProgress(job),
		};

		const currentList = state.supplies.guidelineModel.userForm.jobList;
		const jobList = map(
			when(
				propEq('id', job.id),
				() => ({ ...jobWithProgress }),
			),
			currentList,
		);

		commit('UPDATE_JOB_LIST', jobList);
	},
	async loadJobEvaluationConfig({ commit }) {
		const JOB_EVALUATION_TOOL_CODE = 'JEV';
		const { data } = await Vue.prototype.$http.get(`/tools/${JOB_EVALUATION_TOOL_CODE}`);
		commit('UPDATE_MAIN_DATA', data);
	},
	async loadRoles({ commit, state }) {
		// Evitar cargar si ya están cargados (cache)
		if (state.supplies.guidelineModel.levelSelection.role &&
			typeof state.supplies.guidelineModel.levelSelection.role === 'object' &&
			state.supplies.guidelineModel.levelSelection.role.data &&
			Array.isArray(state.supplies.guidelineModel.levelSelection.role.data) &&
			state.supplies.guidelineModel.levelSelection.role.data.length > 0) {
			return state.supplies.guidelineModel.levelSelection.role;
		}

		const { data } = await Vue.prototype.$httpPraktiko.get('/general-data/ROL');
		const payload = {
			name: 'Rol',
			data,
		};
		commit('UPDATE_LEVEL_SELECTION_ROLE', payload);
		return payload;
	},
	async loadTitles({ commit, state }) {
		// Evitar cargar si ya están cargados (cache)
		if (state.supplies.guidelineModel.levelSelection.title &&
			typeof state.supplies.guidelineModel.levelSelection.title === 'object' &&
			state.supplies.guidelineModel.levelSelection.title.data &&
			Array.isArray(state.supplies.guidelineModel.levelSelection.title.data) &&
			state.supplies.guidelineModel.levelSelection.title.data.length > 0) {
			return state.supplies.guidelineModel.levelSelection.title;
		}

		const { data } = await Vue.prototype.$httpPraktiko.get('/general-data/TIT');
		const payload = {
			name: 'Título',
			data,
		};
		commit('UPDATE_LEVEL_SELECTION_TITLE', payload);
		return payload;
	},
	async loadPlatforms({ commit }) {
		const { data } = await Vue.prototype.$httpPraktiko.get('/general-data/PLTF');
		commit('UPDATE_PLATFORMS', {
			name: 'Plataformas',
			data,
		});
	},
	async loadExperiences({ commit, state }) {
		// Evitar cargar si ya están cargados (cache)
		if (state.supplies.guidelineModel.levelSelection.experience &&
			typeof state.supplies.guidelineModel.levelSelection.experience === 'object' &&
			state.supplies.guidelineModel.levelSelection.experience.data &&
			Array.isArray(state.supplies.guidelineModel.levelSelection.experience.data) &&
			state.supplies.guidelineModel.levelSelection.experience.data.length > 0) {
			return state.supplies.guidelineModel.levelSelection.experience;
		}

		const params = {
			sortField: 'name',
			sortDirection: 'ASC',
		};
		const { data } = await Vue.prototype.$httpPraktiko.get('/general-data/EXP', { params });
		const payload = {
			name: 'Experiencia',
			data,
		};
		commit('UPDATE_LEVEL_SELECTION_EXPERIENCE', payload);
		return payload;
	},
	async loadEnglishLevels({ commit, state }) {
		// Evitar cargar si ya están cargados (cache)
		if (state.supplies.guidelineModel.levelSelection.englishLevel &&
			typeof state.supplies.guidelineModel.levelSelection.englishLevel === 'object' &&
			state.supplies.guidelineModel.levelSelection.englishLevel.data &&
			Array.isArray(state.supplies.guidelineModel.levelSelection.englishLevel.data) &&
			state.supplies.guidelineModel.levelSelection.englishLevel.data.length > 0) {
			return state.supplies.guidelineModel.levelSelection.englishLevel;
		}

		const { data } = await Vue.prototype.$httpPraktiko.get('/general-data/NDI');
		const payload = {
			name: 'Nivel de Inglés',
			data,
		};
		commit('UPDATE_LEVEL_SELECTION_ENGLISH_LEVEL', payload);
		return payload;
	},
	async loadVerbs({ commit, state }) {
		try {
			// Evitar cargar si ya están cargados (cache)
			if (state.supplies.guidelineModel.verbsSelection.highCommand &&
				state.supplies.guidelineModel.verbsSelection.highCommand.details &&
				Array.isArray(state.supplies.guidelineModel.verbsSelection.highCommand.details) &&
				state.supplies.guidelineModel.verbsSelection.highCommand.details.length > 0 &&
				state.supplies.guidelineModel.verbsSelection.mediumCommand &&
				state.supplies.guidelineModel.verbsSelection.mediumCommand.details &&
					Array.isArray(state.supplies.guidelineModel.verbsSelection.mediumCommand.details) &&
				state.supplies.guidelineModel.verbsSelection.mediumCommand.details.length > 0 &&
				state.supplies.guidelineModel.verbsSelection.lowCommand &&
				state.supplies.guidelineModel.verbsSelection.lowCommand.details &&
				Array.isArray(state.supplies.guidelineModel.verbsSelection.lowCommand.details) &&
				state.supplies.guidelineModel.verbsSelection.lowCommand.details.length > 0) {
				return true;
			}

			const params = {
				sortField: 'name',
				sortDirection: 'ASC',
			};
			let { data } = await Vue.prototype.$httpPraktiko.get('/general-data/VER', { params });
			data = data.map((d) => {
				const newD = { ...d };
				newD.details = d.details.sort((a, b) => {
					if (a.description.toLowerCase() > b.description.toLowerCase()) {
						return 1;
					}
					return -1;
				});
				return newD;
			});
			commit('UPDATE_COMMAND_DATA', {
				command: 'highCommand',
				data: find(propEq('code', commandMap.highCommand), data),
			});
			commit('UPDATE_COMMAND_DATA', {
				command: 'mediumCommand',
				data: find(propEq('code', commandMap.mediumCommand), data),
			});
			commit('UPDATE_COMMAND_DATA', {
				command: 'lowCommand',
				data: find(propEq('code', commandMap.lowCommand), data),
			});
			return true;
		} catch (e) {
			throw new Error(e);
		}
	},
	async getGuidelineModelStatus({ commit }) {
		const response = await Vue.prototype.$http.get('/polls/JEV?organizationChart=true');
		const { details, jobs, additionalInformation } = response.data;
		const jobList = (additionalInformation && additionalInformation.jobs) || [];

		// syncing job list from api - agregando cálculo de progreso
		const jobsWithProgress = map((job) => {
			const parsedJob = parseJobs(job);
			return {
				...parsedJob,
				progress: calculateJobProgress(parsedJob),
			};
		}, jobList);
		commit('UPDATE_JOB_LIST', jobsWithProgress);
		// syncing organization chart info from api
		commit('UPDATE_ORGANIZATION_CHART', jobs || []);
		// Derive the REAL father of each job from the stage-2 tree (the same tree the
		// org chart renders). `details` (stage 1) keeps stale default fathers; the tree
		// encodes the true reporting line by nesting. Mirror appearances are emitted by
		// the backend as leaf clones flagged `isMirror`; they are collected separately
		// so the primary father (from the non-mirror edge) is never overwritten. Only
		// record an edge when both parent and child expose an id, so this can never wipe
		// an existing father: a job not found as a child in the tree keeps its `details`
		// father (no regression).
		const fatherByChildId = {};
		const mirrorsByChildId = {};
		const walkTree = (node) => {
			const children = (node && node.details) || [];
			for (let i = 0; i < children.length; i += 1) {
				const child = children[i];
				if (node.id && child.id) {
					if (child.isMirror) {
						if (!mirrorsByChildId[child.id]) {
							mirrorsByChildId[child.id] = [];
						}
						if (mirrorsByChildId[child.id].indexOf(node.id) === -1) {
							mirrorsByChildId[child.id].push(node.id);
						}
					} else {
						fatherByChildId[child.id] = { fatherId: node.id, fatherName: node.name };
					}
				}
				// Mirror clones are leaves; do not descend into them.
				if (!child.isMirror) {
					walkTree(child);
				}
			}
		};
		(jobs || []).forEach(walkTree);
		if (details) {
			const parsedAssignations = parseHierarchyAssignation(details);
			const assignationsWithFathers = map(level => ({
				...level,
				jobPositions: map((jp) => {
					const real = fatherByChildId[jp.id];
					const treeMirrors = mirrorsByChildId[jp.id];
					// Prefer tree-reconstructed mirrors; fall back to the value parsed
					// from stage-1 details when the tree has no mirror data.
					const mirrorFatherIds = treeMirrors || (jp.mirrorFatherIds || []);
					const withFather = real
						? { ...jp, fatherId: real.fatherId, fatherName: real.fatherName }
						: jp;
					return { ...withFather, mirrorFatherIds };
				}, level.jobPositions || []),
			}), parsedAssignations);
			commit('UPDATE_HIERARCHY_ASSIGNATION', assignationsWithFathers);
		}
	},
	async assignHierarchy({ state }, options = {}) {
		bus.$emit(LOADER_EVENT, { show: true, message: LOADER_MESSAGE });
		try {
			const payloadRequest = parseAssignationRequest(state, options);
			await Vue.prototype.$httpPraktiko.post('/polles/unique', payloadRequest);
			return true;
		} finally {
			bus.$emit(LOADER_EVENT, { show: false });
		}
	},
	async generateOrganizationChart({ state }) {
		bus.$emit(LOADER_EVENT, { show: true, message: LOADER_MESSAGE_ORGANIZATIONAL_CHART });
		try {
			await Vue.prototype.$httpPraktiko.post('/polles/unique', parseOrganizationChart(state));
			return true;
		} catch (e) {
			throw e;
		} finally {
			bus.$emit(LOADER_EVENT, { show: false });
		}
	},
	updateAssignation({ state, commit }, { jobPosition, assignationId }) {
		const hierarchyAssignation = path(['supplies', 'guidelineModel', 'hierarchyAssignation'], state);
		const assignationIndex = findIndex(propEq('id', assignationId), hierarchyAssignation);
		const jobPositionIndex = findIndex(propEq('id', jobPosition.id), path([assignationIndex, 'jobPositions'], hierarchyAssignation));
		commit('UPDATE_JOB_POSITION_ASSIGNATION', { assignationIndex, jobPositionIndex, jobPosition });
	},
	filterJobsByGroupId({ commit, state }, groupId) {
		// Validar que groupId sea un array válido
		const validGroupId = Array.isArray(groupId) ? groupId : [];
		const filteredJobs = state.supplies.guidelineModel.userForm.jobList
			.filter(job => (validGroupId.length === 0 ||
				validGroupId.includes(job.areaId)) && job.status);
		const idsJobs = pluck('id', filteredJobs);
		commit('SET_FILTERED_JOBS_IDS', idsJobs);
		commit('SET_FILTERED_JOBS', filteredJobs);
	},
	filterJobActive({ commit }, jobs) {
		const jobsActive = filter(propEq('status', true), jobs);
		const jobFilter = map(prop('id'), jobsActive);
		commit('SET_FILTERED_JOBS_IDS', jobFilter);
	},
	updateFilterJobList({ commit }, filterJobList) {
		commit('UPDATE_FILTER_JOB_LIST', filterJobList);
	},
	jobsDeleteinJobList({ commit, state }, jobSelected) {
		jobSelected.forEach((obj) => {
			const index = state.supplies.guidelineModel.userForm.deletedJobs.findIndex(item => equals(prop('id', item), prop('id', obj)));
			if (index === -1) {
				commit('JOBS_DELETE_IN_JOB_LIST', obj);
			}
		});
	},
	async loadGroups({ commit, state }, { forceRefresh = false } = {}) {
		// Evitar cargar si ya están cargados (cache) y no se fuerza refresh
		if (!forceRefresh && state.supplies.guidelineModel.groups &&
			state.supplies.guidelineModel.groups.length > 0) {
			return state.supplies.guidelineModel.groups;
		}

		// Deduplicate concurrent calls — return the same in-flight promise
		if (pendingLoadGroups) {
			return pendingLoadGroups;
		}

		const params = {
			sortField: 'name',
			sortDirection: 'ASC',
		};
		pendingLoadGroups = Vue.prototype.$httpPraktiko.get('groups', { params })
			.then(({ data: response }) => {
				commit('UPDATE_GROUPS', response);
				return response;
			})
			.finally(() => {
				pendingLoadGroups = null;
			});

		return pendingLoadGroups;
	},

	// ✅ NUEVO: Método para invalidar cache de grupos
	async refreshGroups({ dispatch }) {
		return dispatch('loadGroups', { forceRefresh: true });
	},
};



// WEBPACK FOOTER //
// ./src/modules/job-evaluation/store/guideline-model/actions.js